import { Check, ChevronDown, Plus } from "lucide-react"
import { useEffect, useId, useMemo, useRef, useState } from "react"

import { cn } from "@/lib/cn"

export type OpcionSelector = {
  valor: number
  etiqueta: string
  /** Texto secundario —un RIF, una categoría— que también se busca. */
  detalle?: string
}

type Props = {
  /** El `id` del campo, para que su `<label htmlFor>` lo nombre. */
  id: string
  opciones: readonly OpcionSelector[]
  valor: number | null
  onCambiar: (valor: number | null) => void
  /** Si se da, se envía en un `<input type="hidden">`: sirve dentro de un `FormData`. */
  nombre?: string
  /** La opción «vacía». Sin ella el campo es obligatorio y no se puede dejar en blanco. */
  vacio?: string
  placeholder?: string
  /**
   * Crear lo que no existe sin salir del formulario. Recibe lo tecleado y devuelve el
   * `valor` de lo creado, que queda elegido.
   */
  onCrear?: (texto: string) => Promise<number>
  /** Cómo se nombra lo que se crea: «categoría», «proveedor». */
  queSeCrea?: string
  deshabilitado?: boolean
}

/**
 * Un `<select>` en el que se puede escribir para buscar, y —si se le deja— crear lo
 * que falta.
 *
 * ⚠ **Es el patrón «combobox» de WAI-ARIA 1.2, no un `<div>` que se abre.** El campo
 * es `role="combobox"` y apunta a su lista con `aria-controls`; la opción resaltada
 * viaja en `aria-activedescendant`, así que el foco NO sale del campo mientras se
 * recorre la lista con las flechas. Sin eso, un lector de pantalla no anuncia qué
 * opción se está mirando y quien usa teclado pierde lo que tecleó.
 *
 * ⚠ **La búsqueda no distingue tildes ni mayúsculas.** «linea» encuentra «Línea
 * Naviera»: en un catálogo que vino del 2.0 conviven las dos formas, y buscar con la
 * exacta obligaría a saber cómo la escribió quien la dio de alta.
 *
 * ⚠ **«Crear» sólo aparece si lo tecleado no coincide EXACTAMENTE con una opción**
 * —también sin tildes ni mayúsculas—. Si no, sería la vía más corta para tener
 * «Servicios» y «servicios» como dos categorías, que es justo lo que un catálogo
 * existe para impedir.
 *
 * ⚠ **La lista es `.panel-flotante`, opaca.** Flota sobre el formulario y sobre el
 * cristal; sin fondo propio su texto se leería encima de lo que tapa.
 */
export function SelectorBuscable({
  id,
  opciones,
  valor,
  onCambiar,
  nombre,
  vacio,
  placeholder = "Buscar…",
  onCrear,
  queSeCrea = "elemento",
  deshabilitado,
}: Props) {
  const idLista = useId()
  const caja = useRef<HTMLDivElement>(null)
  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState("")
  const [resaltado, setResaltado] = useState(0)
  const [creando, setCreando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const elegida = opciones.find((o) => o.valor === valor) ?? null

  const filtradas = useMemo(() => {
    const buscado = normalizar(texto)
    if (buscado === "") return opciones
    return opciones.filter((o) => normalizar(`${o.etiqueta} ${o.detalle ?? ""}`).includes(buscado))
  }, [opciones, texto])

  const exacta = opciones.some((o) => normalizar(o.etiqueta) === normalizar(texto))
  const ofrecerCrear = onCrear !== undefined && texto.trim().length >= 2 && !exacta

  // Las filas de la lista, en orden: la vacía, las encontradas y «crear».
  type Fila = { tipo: "vacio" } | { tipo: "opcion"; opcion: OpcionSelector } | { tipo: "crear" }
  const filas: Fila[] = [
    ...(vacio !== undefined && texto === "" ? [{ tipo: "vacio" as const }] : []),
    ...filtradas.map((opcion) => ({ tipo: "opcion" as const, opcion })),
    ...(ofrecerCrear ? [{ tipo: "crear" as const }] : []),
  ]

  // Cerrar al pulsar fuera. `mousedown` y no `click`: el clic llega tarde, cuando el
  // foco ya se fue y el campo se ha vaciado.
  useEffect(() => {
    if (!abierto) return
    function fuera(evento: MouseEvent) {
      if (caja.current && !caja.current.contains(evento.target as Node)) cerrar()
    }
    document.addEventListener("mousedown", fuera)
    return () => document.removeEventListener("mousedown", fuera)
  })

  // ⚠ **Escape en `window` y en CAPTURA, sólo mientras la lista está abierta.** El
  // `Modal` escucha Escape en captura sobre `document` para cerrarse él solo (ver su
  // comentario). Un `onKeyDown` en el campo llega DESPUÉS, en burbuja, y ahí el modal
  // ya se habría cerrado llevándose el formulario a medio rellenar. `window` va
  // antes que `document` en la fase de captura: se corta aquí y el modal no se entera.
  useEffect(() => {
    if (!abierto) return
    function alEscape(evento: KeyboardEvent) {
      if (evento.key !== "Escape") return
      if (!caja.current?.contains(document.activeElement)) return
      evento.stopPropagation()
      cerrar()
    }
    window.addEventListener("keydown", alEscape, true)
    return () => window.removeEventListener("keydown", alEscape, true)
  })

  function cerrar() {
    setAbierto(false)
    setTexto("")
    setResaltado(0)
    setError(null)
  }

  async function elegir(fila: Fila | undefined) {
    if (fila === undefined) return
    if (fila.tipo === "vacio") {
      onCambiar(null)
      cerrar()
      return
    }
    if (fila.tipo === "opcion") {
      onCambiar(fila.opcion.valor)
      cerrar()
      return
    }
    if (onCrear === undefined) return
    setCreando(true)
    setError(null)
    try {
      const nuevo = await onCrear(texto.trim())
      onCambiar(nuevo)
      cerrar()
    } catch (e) {
      setError(e instanceof Error ? e.message : `No se pudo crear el ${queSeCrea}.`)
    } finally {
      setCreando(false)
    }
  }

  function alTeclear(evento: React.KeyboardEvent<HTMLInputElement>) {
    if (evento.key === "ArrowDown") {
      evento.preventDefault()
      setAbierto(true)
      setResaltado((r) => Math.min(r + 1, Math.max(filas.length - 1, 0)))
    } else if (evento.key === "ArrowUp") {
      evento.preventDefault()
      setResaltado((r) => Math.max(r - 1, 0))
    } else if (evento.key === "Enter") {
      // Dentro de un formulario, Enter lo enviaría a medio rellenar.
      if (abierto) {
        evento.preventDefault()
        void elegir(filas[resaltado])
      }
    }
    // Escape lo atiende el oyente de `window`, arriba: aquí llegaría tarde.
  }

  const idFila = (i: number) => `${idLista}-${i}`

  return (
    <div ref={caja} className="relative mt-1">
      {nombre !== undefined && <input type="hidden" name={nombre} value={valor ?? ""} />}
      <div className="relative">
        <input
          id={id}
          role="combobox"
          aria-expanded={abierto}
          aria-controls={idLista}
          aria-autocomplete="list"
          aria-activedescendant={abierto && filas.length > 0 ? idFila(resaltado) : undefined}
          autoComplete="off"
          disabled={deshabilitado}
          className="campo pr-8"
          // Cerrado se lee lo elegido; abierto, lo que se busca.
          value={abierto ? texto : (elegida?.etiqueta ?? (vacio !== undefined ? vacio : ""))}
          placeholder={abierto ? placeholder : (vacio ?? placeholder)}
          onFocus={() => setAbierto(true)}
          onClick={() => setAbierto(true)}
          onChange={(e) => {
            setTexto(e.target.value)
            setResaltado(0)
            setAbierto(true)
          }}
          onKeyDown={alTeclear}
        />
        <ChevronDown
          size={16}
          aria-hidden={true}
          className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2"
          style={{ color: "var(--fg-muted)" }}
        />
      </div>

      {abierto && (
        <div
          id={idLista}
          role="listbox"
          className="panel-flotante absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-[var(--radius-control)] py-1 text-sm"
        >
          {filas.length === 0 && (
            <p className="px-3 py-2" style={{ color: "var(--fg-muted)" }}>
              Nada coincide.
            </p>
          )}
          {filas.map((fila, i) => {
            const activa = i === resaltado
            const elegidaAqui =
              (fila.tipo === "opcion" && fila.opcion.valor === valor) ||
              (fila.tipo === "vacio" && valor === null)
            return (
              <div
                key={fila.tipo === "opcion" ? fila.opcion.valor : fila.tipo}
                id={idFila(i)}
                role="option"
                aria-selected={elegidaAqui}
                tabIndex={-1}
                // `mousedown` para que el campo no pierda el foco antes de elegir.
                onMouseDown={(e) => {
                  e.preventDefault()
                  void elegir(fila)
                }}
                onMouseEnter={() => setResaltado(i)}
                className={cn(
                  "flex cursor-pointer items-center gap-2 px-3 py-1.5",
                  activa && "activo-cristal",
                )}
              >
                {fila.tipo === "crear" ? (
                  <>
                    <Plus size={14} aria-hidden={true} />
                    <span>{creando ? "Creando…" : `Crear ${queSeCrea} «${texto.trim()}»`}</span>
                  </>
                ) : (
                  <>
                    <Check
                      size={14}
                      aria-hidden={true}
                      style={{ visibility: elegidaAqui ? "visible" : "hidden" }}
                    />
                    {/* ⚠ El detalle va DEBAJO y no al lado. Compartiendo la línea,
                        un detalle largo —la tarifa de un método de pago— se queda
                        con todo el ancho y el nombre se trunca hasta desaparecer:
                        la lista enseñaba descripciones sin decir de qué método. */}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        {fila.tipo === "vacio" ? vacio : fila.opcion.etiqueta}
                      </span>
                      {fila.tipo === "opcion" && fila.opcion.detalle && (
                        <span
                          className="block truncate text-xs"
                          style={{ color: "var(--fg-muted)" }}
                        >
                          {fila.opcion.detalle}
                        </span>
                      )}
                    </span>
                  </>
                )}
              </div>
            )
          })}
          {error && (
            <p role="alert" className="px-3 py-2 text-xs" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Sin tildes, sin mayúsculas y sin espacios de sobra: «Línea» y «linea» son lo mismo. */
export function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
}
